import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  styled,
  Tooltip as MuiTooltip,
  type TooltipProps as MuiTooltipProps,
} from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { type AnchorHTMLAttributes, forwardRef, useId } from 'react';

import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';

const TooltipAnchor = styled('span')({ display: 'inline-flex' });

type Props = WithoutStyleOverrides<Omit<MuiIconButtonProps, 'ref'>>
  & Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel' | 'download'> & {
    tooltip: string;
    tooltipPlacement?: MuiTooltipProps['placement'];
  };

// forwardRef, not a `ref` prop: the app still runs React 18, where function components never receive `ref`.
export const IconButton = forwardRef<HTMLElement, Props>(function IconButton(props, ref) {
  const elementRef = useForkRef(ref);
  const { tooltip, tooltipPlacement, ...iconButtonProps } = props;
  const tooltipId = useId();
  const ownLabel = iconButtonProps['aria-label'];
  const hasOwnLabel = ownLabel !== undefined || iconButtonProps['aria-labelledby'] !== undefined;
  const describedByTooltip = hasOwnLabel && ownLabel !== tooltip;
  const describedBy = [iconButtonProps['aria-describedby'], describedByTooltip ? tooltipId : undefined]
    .filter((id) => id !== undefined)
    .join(' ');

  return (
    <MuiTooltip id={tooltipId} title={tooltip} placement={tooltipPlacement} describeChild>
      <TooltipAnchor>
        <MuiIconButton
          {...iconButtonProps}
          aria-label={hasOwnLabel ? ownLabel : tooltip}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          ref={elementRef}
        />
      </TooltipAnchor>
    </MuiTooltip>
  );
});

export type { Props as IconButtonProps };
